import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GeoBrowseDisease } from '../../types';
import { useGeoBrowseContext } from '../../context/GeoBrowseContext';
import { useGeoBrowseQuery } from '../../hooks/useGeoBrowseThresholds';
import { fetchDiseases } from '../../services/geoBrowseApi';
import { formatDiseaseName, formatNumber } from '../../utils/formatters';
import BrowseDataTable, { type BrowseColumn } from '../BrowseDataTable';
import PageHeader from '../PageHeader';
import StatCards from '../StatCards';
import { InlineBar } from '../ui/GeoBrowseUi';
import styles from '../../GeoBrowse.module.css';

export default function DiseasesView() {
  const navigate = useNavigate();
  const { thresholds, refreshKey, mergePathWithSearchParams } = useGeoBrowseContext();
  const { data, loading, error } = useGeoBrowseQuery(
    () => fetchDiseases(thresholds),
    [thresholds.padj, thresholds.fc, refreshKey],
  );

  const maxGenesSig = useMemo(
    () => Math.max(1, ...(data ?? []).map(d => d.n_genes_sig)),
    [data],
  );

  const maxDegStudies = useMemo(
    () => Math.max(1, ...(data ?? []).map(d => d.n_studies_with_deg)),
    [data],
  );

  const summary = useMemo(() => {
    if (!data?.length) return null;
    const totalStudiesDeg = data.reduce((s, d) => s + d.n_studies_with_deg, 0);
    const totalStudies = data.reduce((s, d) => s + d.n_studies_total, 0);
    const totalSamples = data.reduce((s, d) => s + d.n_samples_total, 0);
    return {
      diseases: data.length,
      studiesDeg: totalStudiesDeg,
      studiesTotal: totalStudies,
      samples: totalSamples,
      genesTested: data[0]?.n_genes_tested ?? 37663,
    };
  }, [data]);

  const columns: BrowseColumn<GeoBrowseDisease>[] = useMemo(
    () => [
      {
        id: 'cui',
        label: 'cui',
        sortValue: r => r.cui,
        accessor: r => r.cui,
      },
      {
        id: 'cui_name',
        label: 'cui_name',
        sortValue: r => r.cui_name,
        accessor: r => formatDiseaseName(r.cui_name),
        render: r => formatDiseaseName(r.cui_name),
      },
      {
        id: 'n_studies_total',
        label: 'n_studies_total',
        sortValue: r => r.n_studies_total,
        accessor: r => r.n_studies_total,
      },
      {
        id: 'n_studies_usable',
        label: 'n_studies_usable',
        sortValue: r => r.n_studies_usable,
        accessor: r => r.n_studies_usable,
      },
      {
        id: 'n_studies_with_deg',
        label: 'n_studies_with_deg',
        sortValue: r => r.n_studies_with_deg,
        filterValue: r => r.n_studies_with_deg,
        render: r => (
          <InlineBar value={r.n_studies_with_deg} max={maxDegStudies} tone="green" />
        ),
      },
      {
        id: 'n_samples_total',
        label: 'n_samples_total',
        sortValue: r => r.n_samples_total,
        accessor: r => formatNumber(r.n_samples_total),
      },
      {
        id: 'n_genes_tested',
        label: 'n_genes_tested',
        sortValue: r => r.n_genes_tested,
        accessor: r => formatNumber(r.n_genes_tested),
      },
      {
        id: 'n_genes_sig',
        label: 'n_genes_sig',
        sortValue: r => r.n_genes_sig,
        filterValue: r => r.n_genes_sig,
        render: r => <InlineBar value={r.n_genes_sig} max={maxGenesSig} tone="blue" />,
      },
    ],
    [maxGenesSig, maxDegStudies],
  );

  if (loading) return <div className={styles.loading}>Loading diseases…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data?.length) return <div className={styles.empty}>No diseases found.</div>;

  return (
    <>
      <PageHeader
        title="Diseases"
        description="Click a row to filter the Studies tab to that disease."
      />

      {summary && (
        <StatCards
          items={[
            { label: 'Diseases', value: summary.diseases },
            {
              label: 'Studies with DEG',
              value: formatNumber(summary.studiesDeg),
              sub: `of ${formatNumber(summary.studiesTotal)} indexed`,
            },
            { label: 'Samples', value: formatNumber(summary.samples) },
            {
              label: 'Genes tested',
              value: formatNumber(summary.genesTested),
              sub: 'shared reference set',
            },
          ]}
        />
      )}

      <BrowseDataTable
        rows={data}
        columns={columns}
        rowKey={r => r.cui}
        searchPlaceholder=""
        defaultPageSize={25}
        defaultSort={{ columnId: 'n_genes_sig', direction: 'desc' }}
        searchFilter={(row, q) =>
          `${row.cui_name} ${row.cui}`.toLowerCase().includes(q)
        }
        onRowClick={row =>
          navigate(mergePathWithSearchParams('/geo-browse/studies', { cui: row.cui }))
        }
      />
    </>
  );
}
